import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CircularProgress,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
} from "@material-ui/core";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";

import { MUTATIONS, QUERIES } from "../../../graphql";
import {
  Apartment,
  CommunicationCommunicationGroup,
  CommunicationGroupUser,
  CommunicationTarget,
  computeUserVisualName,
  User,
} from "../../../graphql/models";
import { GqlRecordPicker, UserListDetail } from "../";
import { useSnackbarMutation } from "../../../hooks";
import useCommunicationTargetsPickerStyle from "./communication_targets_picker.style";
import { CommunicationTargetCard } from "../communication_target_card";

export interface CommunicationTargetsPickerProps extends PropsWithChildren<{}> {
  disabled?: boolean;
  groups: CommunicationCommunicationGroup[];
  groupsChange: (
    groups: CommunicationCommunicationGroup[],
    computedTargets: CommunicationTarget[]
  ) => void;
  targets: CommunicationTarget[];
  targetsChange: (targets: CommunicationTarget[]) => void;
}

export function CommunicationTargetsPicker(
  props: CommunicationTargetsPickerProps
) {
  const { disabled, groups, groupsChange, targets, targetsChange, children } =
    props;
  const [selectedGroups, setSelectedGroups] = useState<
    CommunicationCommunicationGroup[]
  >(groups ?? []);
  const [apartments, setSelectedApartments] = useState<Apartment[]>([]);
  const [users, setSelectedUsers] = useState<User[]>([]);
  const [activeStep, setActiveStep] = useState(0);

  // Effect: in case any target is presente, display only the last ste.
  useEffect(() => {
    if (targets && targets.length > 0) {
      setActiveStep(3);
    }
  }, [targets]);

  const steps = useMemo(
    () => [
      "Scegli a chi vuoi inviare la comunicazione",
      "Seleziona i gruppi di comunicazione",
      "Scegli manualmente gli appartamenti o gli utenti a cui inviare la comunicazione",
      "Verifica i contatti selezionati",
    ],
    []
  );

  const triggerSetSelectedGroups = (
    value: CommunicationCommunicationGroup[]
  ) => {
    setSelectedGroups(value);
  };

  const classes = useCommunicationTargetsPickerStyle();

  const triggerTargetsChange = useCallback(
    (targets: CommunicationTarget[]) => {
      if (users.length > 0) {
        const userTargets: CommunicationTarget[] = users.map((v) => ({
          userId: v.id,
          name: computeUserVisualName(v),
          email: v.email,
        }));

        targetsChange([...targets, ...userTargets]);
      } else {
        targetsChange(targets);
      }
    },
    [targetsChange, users]
  );

  const [invokeComputeCommunicationGroupTargets, { loading }] =
    useSnackbarMutation(MUTATIONS.COMPUTE_COMMUNICATION_GROUP_TARGETS, {
      onCompleted: ({ computeCommunicationGroupTargets }) => {
        if (computeCommunicationGroupTargets) {
          groupsChange(selectedGroups, computeCommunicationGroupTargets);
          triggerTargetsChange(computeCommunicationGroupTargets);
          setActiveStep(3);
        }
      },
    });

  const [
    invokeComputeApartmentsCommunicationTargets,
    { loading: loadingApartmentTargets },
  ] = useSnackbarMutation(MUTATIONS.COMPUTE_COMMUNICATION_APARTMENTS_TARGETS, {
    onCompleted: ({ computeApartmentCommunicationTargets }) => {
      if (computeApartmentCommunicationTargets) {
        triggerTargetsChange(computeApartmentCommunicationTargets);
        setActiveStep(3);
      }
    },
  });

  const triggerSelectedApartmentsChange = (apartments: Apartment[]) => {
    setSelectedApartments(apartments);
  };
  const loadTargetsFromApartments = () => {
    if (apartments.length > 0) {
      invokeComputeApartmentsCommunicationTargets({
        variables: {
          apartmentIds: apartments.map((v) => v.id),
        },
      });
    } else {
      triggerTargetsChange([]);
      setActiveStep(3);
    }
  };

  const loadTargetsFromGroups = () => {
    invokeComputeCommunicationGroupTargets({
      variables: {
        communicationGroupsIds: selectedGroups.map((cg) => cg.id),
      },
    });
  };

  const triggerResetTargets = () => {
    targetsChange([]);
    setActiveStep(0);
  };

  return (
    <Paper className={classes.root}>
      <Grid container spacing={3}>
        <Grid item sm={12} xs={12}>
          <Stepper activeStep={activeStep}>
            <Step key={steps[0]} completed={activeStep > 0}>
              <StepLabel>{steps[0]}</StepLabel>
            </Step>
            <Step key={steps[1]} completed={activeStep > 1}>
              <StepLabel>{steps[1]}</StepLabel>
            </Step>
            <Step
              key={steps[2]}
              disabled={selectedGroups.length > 0}
              completed={activeStep > 2}
            >
              <StepLabel>{steps[2]}</StepLabel>
            </Step>
            <Step key={steps[3]} completed={activeStep > 3}>
              <StepLabel>{steps[3]}</StepLabel>
            </Step>
          </Stepper>
        </Grid>
        <Grid
          item
          sm={12}
          xs={12}
          style={{
            padding: "2rem",
          }}
        >
          {activeStep === 0 && (
            <div
              className={classes.width100}
              style={{
                display: "flex",
                gap: "1rem",
                flexDirection: "row",
              }}
            >
              <Card
                variant="outlined"
                elevation={3}
                style={{ padding: "3rem" }}
              >
                <CardContent>
                  <Typography variant="h5">
                    Voglio inviare la comunicazione ad uno o più gruppi
                  </Typography>
                  <br />
                  <Grid container spacing={3}>
                    <Typography variant="h6" color="textSecondary">
                      Scegli questa opzione se è già stato creato un gruppo di
                      destinatari nella voce di menu "Gruppi di comunicazioni".
                      <br />
                      La procedura ti consentirà di scegliere uno o più gruppi e
                      la comunicazione verrà inviata a tutti i condomini
                      presenti nel gruppo.
                    </Typography>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button
                    onClick={() => setActiveStep(1)}
                    variant="contained"
                    color="primary"
                  >
                    PROCEDI SCEGLIENDO I GRUPPI
                  </Button>
                </CardActions>
              </Card>
              <Card
                variant="outlined"
                elevation={3}
                style={{ padding: "3rem" }}
              >
                <CardContent>
                  <Typography variant="h5">
                    Voglio inviare la comunicazione a degli appartmenti
                    specifici
                  </Typography>
                  <br />
                  <Grid container spacing={3}>
                    <Typography variant="h6" color="textSecondary">
                      Scegli questa opzione se non sai se esiste un gruppo
                      oppure se vuoi inviare la comunicazione a dei condomini /
                      appartamenti specifici.
                    </Typography>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button
                    onClick={() => setActiveStep(2)}
                    variant="contained"
                    color="primary"
                  >
                    PROCEDI SCEGLIENDO I SINGOLI APPARTAMENTI E/O UTENTI
                  </Button>
                </CardActions>
              </Card>
            </div>
          )}
          {activeStep === 1 && (
            <Grid item xs={12} xl={12}>
              <GqlRecordPicker
                disabled={disabled || loading}
                className={classes.width100}
                multiple={true}
                value={selectedGroups}
                getOptionLabel={(record) => record.name}
                placeHolder={"Gruppi"}
                renderOption={(record) => record.name}
                onValueChange={(value) => triggerSetSelectedGroups(value)}
                query={QUERIES.COMMUNICATION_GROUPS}
                queryResultDataField={"communicationGroups"}
              />
            </Grid>
          )}
          {activeStep === 2 && (
            <Grid item xs={12} xl={12}>
              <GqlRecordPicker
                disabled={disabled || loading}
                className={classes.width100}
                multiple={true}
                value={apartments}
                getOptionLabel={(record: Apartment) => record.code}
                placeHolder={"Appartamenti"}
                renderOption={(record: Apartment) => record.code}
                onValueChange={(value: Apartment[]) =>
                  triggerSelectedApartmentsChange(value)
                }
                query={QUERIES.APARTMENTS}
                queryResultDataField={"apartments"}
              />
              <br />
              <GqlRecordPicker
                className={classes.width100}
                multiple={true}
                value={users}
                getOptionLabel={(record: CommunicationGroupUser | User) => {
                  // console.log("record is", record);
                  if ((record as CommunicationGroupUser)?.fullName) {
                    return (record as CommunicationGroupUser).fullName ?? "";
                  } else {
                    return computeUserVisualName(record as User);
                  }
                }}
                placeHolder={"Utenti"}
                renderOption={(record: User) => (
                  <UserListDetail user={record} />
                )}
                onValueChange={setSelectedUsers}
                query={QUERIES.USERS}
                queryResultDataField={"users"}
              />
            </Grid>
          )}
          {activeStep === 3 && (
            <Grid container spacing={3}>
              {targets.map((target) => (
                <Grid spacing={1} item xl={3} lg={4} md={6} xs={12}>
                  <CommunicationTargetCard target={target} />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
        <Grid item sm={12} xs={12} style={{ padding: "2rem" }}>
          {activeStep === 1 && (
            <Grid container>
              <Grid item xs={12} lg={2} xl={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled}
                  onClick={triggerResetTargets}
                  size="small"
                >
                  {!loading ? "Torna indietro" : <CircularProgress />}
                </Button>
              </Grid>
              <Grid item xs={12} lg={2} xl={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled || selectedGroups.length === 0}
                  onClick={loadTargetsFromGroups}
                  size="small"
                >
                  {!loading ? "Avanti" : <CircularProgress />}
                </Button>
              </Grid>
            </Grid>
          )}
          {activeStep === 2 && (
            <Grid container>
              <Grid item xs={12} lg={2} xl={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled}
                  onClick={triggerResetTargets}
                  size="small"
                >
                  {!loadingApartmentTargets ? (
                    "Torna indietro"
                  ) : (
                    <CircularProgress />
                  )}
                </Button>
              </Grid>
              <Grid item xs={12} lg={2} xl={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    disabled || (apartments.length === 0 && users.length === 0)
                  }
                  onClick={loadTargetsFromApartments}
                  size="small"
                >
                  {!loadingApartmentTargets ? "Avanti" : <CircularProgress />}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      {children}
    </Paper>
  );
}
